import { useState } from 'react'
import toast from 'react-hot-toast'
import {
  estimateMissionRhum,
  useRaidEstimate,
  useLaunchRaid,
  useMyFleet,
  useMyMissions,
  ongoingMissionTo,
  type MapIslandDto,
} from '@/api/hooks'
import { CombatPrimer } from '@/components/CombatPrimer'
import { dangerTier } from '@/lib/danger'
import { raidFlagHint } from '@/lib/raidAlignment'
import { lossSeverity, LOSS_SEVERITY_CLASS, formatAvgLosses, formatLossRange } from '@/lib/raidLosses'
import { HintBubble, ResourceTally } from '@/components/game/ui'
import { GoldIcon, RumIcon, CombatIcon } from '@/components/game/GameIcons'
import { toggleShipSelection, selectManyShips } from '@/components/game/map/fleetSelection'
import {
  RationsLabel,
  ShipPickerList,
  ThreatReadout,
} from '@/components/game/map/launcherBits'
import { combatMeta } from '@/components/game/map/shipPicker/ShipPickerRow'
import { missionFleetCap } from '@/lib/fleetGroups'
import { tripDurationSeconds, TripDuration, convoySpeedPerHour, ConvoyPace } from '@/components/game/map/tripDuration'

export function RaidLauncher({ island }: { island: MapIslandDto }) {
  const { data: fleet } = useMyFleet()
  const { data: missionsData } = useMyMissions()
  const launch = useLaunchRaid()

  // One raid at a time per island: lock a second launch while one is under way
  // (the backend rejects it too, 409).
  const ongoing = ongoingMissionTo(missionsData?.missions ?? [], island.id, 'raid_pnj')
  const dockedShips = fleet?.ships.filter((s) => s.status === 'docked') ?? []
  const fleetCap = missionFleetCap(fleet?.shipyard?.level)

  // Selection state: ordered list of ship ids (order matters — first one is
  // the "lead", and the engine's tiebreakers walk in input order).
  const [selectedIds, setSelectedIds] = useState<number[]>([])

  const selected = selectedIds
    .map((id) => dockedShips.find((s) => s.id === id))
    .filter((s): s is NonNullable<typeof s> => Boolean(s))

  // Live win-rate estimate from a backend combat simulation of this exact fleet.
  const estimate = useRaidEstimate(selectedIds, island.id, selectedIds.length > 0)

  const toggleShip = (id: number) => {
    setSelectedIds((prev) =>
      toggleShipSelection(prev, id, dockedShips, fleetCap, () => toast.error(`Flotte au max (${fleetCap} navires).`)),
    )
  }

  const selectMany = (ids: number[]) => {
    setSelectedIds((prev) =>
      selectManyShips(prev, ids, dockedShips, fleetCap, () => toast.error(`Flotte au max (${fleetCap} navires).`)),
    )
  }

  if (dockedShips.length === 0) {
    return (
      <div className="bg-rum-700/30 rounded-lg p-3 space-y-2 border border-rum-500/30">
        <p className="text-xs text-rum-500 italic">
          Aucun navire à quai pour ce raid.
        </p>
      </div>
    )
  }

  const danger = island.npcDanger ?? 1
  const tier = dangerTier(danger)

  // Aggregate the fleet's contribution per phase + best pierce per phase.
  const totals = selected.reduce(
    (acc, s) => {
      const cs = s.combatStats
      acc.manoeuvre += cs.manoeuvre
      acc.bordee += cs.bordee
      acc.abordage += cs.abordage
      acc.maxBordeePierce = Math.max(acc.maxBordeePierce, cs.bordee)
      acc.maxAbordagePierce = Math.max(acc.maxAbordagePierce, cs.abordage)
      return acc
    },
    { manoeuvre: 0, bordee: 0, abordage: 0, maxBordeePierce: 0, maxAbordagePierce: 0 },
  )

  // Real intel from the backend (A7), with a defensive fallback if absent.
  const threat = island.threat
  const enemyMaxArmure = threat?.maxArmure ?? (danger >= 8 ? 3 : danger >= 3 ? 2 : 1)
  const canPierceBordee = totals.maxBordeePierce >= enemyMaxArmure
  const canPierceAbordage = totals.maxAbordagePierce >= enemyMaxArmure
  const willRicochet = selected.length > 0 && !canPierceBordee && !canPierceAbordage

  // Rhum upkeep estimate: slowest ship of the fleet drives the duration
  // (matches the backend's roundTripSeconds), then we sum each ship's crew
  // × hours. Approximate, the server is authoritative at launch.
  const origin = fleet?.island
  const oneWayDistance = origin ? Math.sqrt((island.x - origin.x) ** 2 + (island.y - origin.y) ** 2) : 0
  const slowestSpeed = convoySpeedPerHour(selected, fleet?.travelSpeedForgeMult ?? 1)
  const roundTripHours = slowestSpeed > 0 ? (oneWayDistance * 2) / slowestSpeed : 0
  const tripSeconds = origin ? tripDurationSeconds(slowestSpeed, origin, island) : 0
  const rhumEstimate = estimateMissionRhum(
    selected,
    Math.max(60, roundTripHours * 3600),
  )
  const rhumAvailable = fleet?.resources?.rum ?? 0
  const notEnoughRhum = selected.length > 0 && rhumEstimate > rhumAvailable

  // Loot preview: /api/raids/estimate now returns the per-type NET haul a
  // victory would pay (a scierie pays in wood, a distillerie in rhum, a comptoir
  // in gold, a lair in a mix), scaled by the player's current reputation (method
  // bonus + an outlaw premium on lawful targets) and already net of the crew
  // wage — all computed server-side (the front never copies balance constants).
  // We render estimate.data.loot as icons when a fleet is selected, and fall
  // back to a qualitative blurb when there's no selection / no estimate yet.

  return (
    <div className="bg-rum-700/25 rounded-lg p-3 space-y-3 border border-rum-500/30">
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-1">
          <p className="text-xs uppercase tracking-wider flex items-center gap-1.5">
            <CombatIcon className="w-4 h-4 text-rum-500" /><span className="text-rum-500">Raid</span>
          </p>
          <p className="flex items-center gap-2">
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[11px] font-semibold uppercase tracking-wider ${tier.badge}`}>
              {tier.label}
            </span>
            <span className="text-xs text-ocean-400 tabular-nums">danger {danger}/10</span>
          </p>
          <p className="text-[11px] text-ocean-300 leading-snug">⚓ {tier.advice}</p>
        </div>
        <p className="text-xs text-ocean-300 whitespace-nowrap">
          <span className="group/tip relative cursor-help underline decoration-dotted decoration-ocean-500/50 underline-offset-2">
            Cap flotte : <strong className="text-sand-300">{fleetCap}</strong>
            <HintBubble
              placement="bottom"
              align="end"
              className="w-56"
              label={
                <>
                  Nombre max de navires engageables dans un raid : <span className="text-sand-200">2 × le niveau du Chantier naval</span>. Améliore-le pour lancer de plus grosses flottes.
                </>
              }
            />
          </span>
        </p>
      </div>

      {threat && <ThreatReadout threat={threat} />}

      <ShipPickerList
        ships={dockedShips}
        selectedIds={selectedIds}
        onToggle={toggleShip}
        cap={fleetCap}
        onSelectMany={selectMany}
        onClearAll={() => setSelectedIds([])}
        meta={combatMeta}
      />

      {selected.length > 0 && (
        <div className="text-xs space-y-1 bg-ocean-900/50 rounded p-2 border border-sand-500/15">
          <div className="flex justify-between text-ocean-200 font-display tabular-nums">
            <span>Manoeuvre / Bordée / Abordage</span>
            <span>{totals.manoeuvre} dés · {totals.bordee} dés · {totals.abordage} dés</span>
          </div>
          <div className="flex justify-between text-ocean-300">
            <span>Pénétration max (B/A)</span>
            <span className="tabular-nums">{totals.maxBordeePierce} / {totals.maxAbordagePierce}</span>
          </div>
          <div className="flex justify-between text-ocean-300">
            <RationsLabel />
            <span className={`tabular-nums inline-flex items-center gap-1 ${notEnoughRhum ? 'text-red-400 font-semibold' : 'text-sand-300'}`}>
              ≈ {rhumEstimate} <RumIcon className="w-3.5 h-3.5" />
              {notEnoughRhum && <span className="ml-1">(banque : {rhumAvailable})</span>}
            </span>
          </div>
          <div className="flex justify-between text-ocean-300">
            <span>Trajet</span>
            <TripDuration seconds={tripSeconds} />
          </div>
          <ConvoyPace ships={selected} tripSeconds={tripSeconds} />
          <div className="border-t border-sand-500/15 pt-1.5 mt-1.5 space-y-1">
            <div className="flex justify-between items-center">
              <span className="text-ocean-200">Chance de victoire</span>
              {estimate.isFetching || estimate.data === undefined ? (
                <span className="text-ocean-400 text-[11px]">simulation…</span>
              ) : (
                <span
                  className={`font-display tabular-nums ${
                    estimate.data.winRate >= 0.6
                      ? 'text-green-300'
                      : estimate.data.winRate >= 0.35
                        ? 'text-sand-300'
                        : 'text-rum-500'
                  }`}
                >
                  ≈ {Math.round(estimate.data.winRate * 100)}%
                </span>
              )}
            </div>
            {estimate.data !== undefined && !estimate.isFetching && (
              <>
                <div className="flex justify-between items-center">
                  <span className="group/tip relative cursor-help text-ocean-200 underline decoration-dotted decoration-ocean-500/50 underline-offset-2">
                    Pertes estimées
                    <HintBubble
                      className="w-64"
                      label={
                        <>
                          Fourchette où tombent tes pertes ~8 fois sur 10 (en moyenne {formatAvgLosses(estimate.data.avgLosses)}). Une <span className="text-sand-200">victoire</span> veut dire que l'ennemi est anéanti, <span className="text-sand-200">pas</span> que tu rentres intact.
                        </>
                      }
                    />
                  </span>
                  <span
                    className={`font-display tabular-nums ${LOSS_SEVERITY_CLASS[lossSeverity(estimate.data.lossesHigh, estimate.data.fleetSize)]}`}
                  >
                    {formatLossRange(estimate.data.lossesLow, estimate.data.lossesHigh)}
                  </span>
                </div>
                {lossSeverity(estimate.data.lossesHigh, estimate.data.fleetSize) !== 'none' && (
                  <p className="text-ocean-400 text-[11px] leading-snug">
                    ⚓ Vaincre = couler l'ennemi, pas en sortir indemne. Tes coques les moins blindées encaissent en premier.
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* What this act makes of you. Reputation is emergent from deeds, which only
          works if the deed announces itself BEFORE it is done: until now the panel
          previewed the loot and the losses but never the allegiance, so the most
          identity-defining button in the game was also its most opaque. Direction
          only, never a magnitude: the numbers stay in the domain. */}
      {raidFlagHint(island.type) !== null && (
        <p className="text-[11px] text-ocean-300 leading-snug">{raidFlagHint(island.type)}</p>
      )}

      <div className="text-xs text-ocean-200 space-y-1">
        {estimate.data?.loot !== undefined ? (
          <>
            {/* Native per-target haul: a dominant resource (scierie→bois, distillerie→
                rhum, comptoir/port→or, repaire→mix) + a small gold appoint. The crew
                takes its cut from the GOLD only — the cargo (bois/rhum) is never taxed.
                "Tu reçois" is server-computed and ALREADY net. */}
            <p className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-wider text-ocean-300">Tu reçois si victoire</span>
              <ResourceTally
                gold={estimate.data.loot.gold || undefined}
                wood={estimate.data.loot.wood || undefined}
                rhum={estimate.data.loot.rhum || undefined}
                className="text-sand-300 font-semibold"
              />
            </p>
            {(estimate.data.crewWage ?? 0) > 0 && (
              <p className="text-[11px] text-ocean-300 flex items-center gap-1.5 flex-wrap tabular-nums">
                <span className="inline-flex items-center gap-1">
                  butin pillé
                  <ResourceTally
                    gold={(estimate.data.loot.gold + (estimate.data.crewWage ?? 0)) || undefined}
                    wood={estimate.data.loot.wood || undefined}
                    rhum={estimate.data.loot.rhum || undefined}
                    className="text-ocean-200"
                  />
                </span>
                <span aria-hidden="true" className="text-ocean-500">·</span>
                <span className="inline-flex items-center gap-1 text-ocean-400">
                  <span aria-hidden="true">⚓</span> part d'équipage{' '}
                  <span className="tabular-nums">−{(estimate.data.crewWage ?? 0).toLocaleString('fr-FR')}</span>
                  <GoldIcon className="w-3 h-3" />
                </span>
              </p>
            )}
            {/* Only levers that actually pay are shown. The reputation premium grows
                with the depth ALREADY reached, so it is exactly 0 on a fresh captain's
                first raid: announcing "réput +0%" turned the sole mention of reputation
                on this screen into a statement that the founding act pays nothing. */}
            {((estimate.data.reputationBonus ?? 0) > 0 || (estimate.data.hullBonus ?? 0) > 0) && (
              <p className="text-[11px] text-ocean-400 flex items-center gap-1.5 flex-wrap tabular-nums">
                <span>butin gonflé par</span>
                {(estimate.data.reputationBonus ?? 0) > 0 && (
                  <span
                    className="font-semibold text-sand-300"
                    title="Prime de réputation : ton allégeance (Doctrine + notoriété hors-la-loi) gonfle le butin pillé."
                  >
                    réput +{Math.round(((estimate.data.reputationBonus ?? 0) / (estimate.data.base || 1)) * 100)}%
                  </span>
                )}
                {(estimate.data.hullBonus ?? 0) > 0 && (
                  <>
                    {(estimate.data.reputationBonus ?? 0) > 0 && (
                      <span aria-hidden="true" className="text-ocean-500">·</span>
                    )}
                    <span
                      className="font-semibold text-sand-300"
                      title="Bonus de flotte : calculé sur la puissance MOYENNE de tes coques, pas sur leur nombre. Une flotte de frégates, galions ou légendaires pille davantage qu'une nuée de bricks ; ajouter un brick à une flotte lourde fait baisser la moyenne, donc le bonus."
                    >
                      flotte +{Math.round(((estimate.data.hullBonus ?? 0) / (((estimate.data.base ?? 0) + (estimate.data.reputationBonus ?? 0)) || 1)) * 100)}%
                    </span>
                  </>
                )}
              </p>
            )}
            <p className="text-ocean-400 text-[11px] leading-snug">
              L'équipage ne prend que des pièces, jamais ta cargaison (bois/rhum).
            </p>
          </>
        ) : (
          <p className="text-ocean-300 leading-snug">
            Butin si victoire : un raid rapporte surtout la ressource de la cible (scierie → bois,
            distillerie → rhum, comptoir/port → or, repaire → un mélange) plus un peu d'or. Plus la
            cible est dangereuse et la traversée longue, plus le butin est gros ; une flotte plus
            puissante pille davantage. L'équipage prélève une part fixe sur les pièces.
          </p>
        )}
        <p className="text-ocean-300">Tout navire coulé est perdu définitivement.</p>
      </div>

      {willRicochet && (
        <p className="text-xs text-rum-500 italic border-l-2 border-rum-500 pl-2">
          Aucun navire ne perce l'armure attendue ({enemyMaxArmure}). Tes boulets ricocheront. Emmène une {enemyMaxArmure === 3 ? 'Frégate ou un Galion' : 'Frégate'}.
        </p>
      )}

      <CombatPrimer />

      {ongoing && (
        <p className="text-xs text-sand-300 flex items-start gap-1.5">
          <CombatIcon className="w-4 h-4 shrink-0 mt-0.5 animate-pulse" />
          <span>
            Un raid est déjà en cours vers cette île
            {ongoing.shipName ? ` (${ongoing.shipName})` : ''}.
          </span>
        </p>
      )}

      <button
        onClick={() => {
          if (selected.length === 0) return
          const shipIds = selected.map((s) => s.id)
          launch.mutate(
            { shipIds, islandId: island.id },
            {
              onSuccess: () =>
                toast.success(`${selected.length} navire(s) en route vers ${island.name}.`),
              onError: (err) =>
                toast.error(err.message || 'Raid impossible'),
            },
          )
          setSelectedIds([])
        }}
        disabled={launch.isPending || selected.length === 0 || notEnoughRhum || ongoing != null}
        title={ongoing ? 'Un raid est déjà en cours vers cette île.' : undefined}
        className="w-full text-sm px-3 py-1.5 rounded-md bg-rum-500 text-ocean-50 font-semibold hover:bg-rum-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5"
      >
        {ongoing ? (
          'Raid en cours'
        ) : launch.isPending ? (
          'Lancement…'
        ) : notEnoughRhum ? (
          'Rhum insuffisant'
        ) : (
          <><CombatIcon className="w-4 h-4" /> Lancer la flotte ({selected.length})</>
        )}
      </button>
    </div>
  )
}
